/**
 * Mertens Exposure Fusion — multi-resolution pyramid blend.
 *
 * Reference:
 *   Mertens, Kautz, Van Reeth — "Exposure Fusion" (PG 2007 / CGF 2009).
 *   See `docs/HDR-DESIGN.md` for the citation graph.
 *
 * NOTE on naming: this is **exposure fusion**, not radiance HDR. The
 * output is a single tone-mapped LDR-like image that *looks* HDR by
 * merging well-exposed regions across brackets — there is never an
 * intermediate radiance map. The public API still calls itself
 * "fuseBrackets" / "HDR Merge" because that's the user-facing concept,
 * but downstream code MUST NOT treat the output as scene radiance.
 *
 * Why pyramid blending instead of weighted average:
 *   A naive `Σ(weight_i × image_i) / Σ(weight_i)` produces ugly seams
 *   wherever the dominant bracket switches abruptly. Building a Laplacian
 *   pyramid per bracket and a Gaussian pyramid per weight, then merging
 *   level-by-level, produces seamless transitions because each frequency
 *   band gets blended at its own scale.
 *
 * Output domain:
 *   Returns a Float32 RGBA buffer in **linear ACEScg**, alpha=1.0.
 *   Values are in [0,1] in the typical case but may dip slightly outside
 *   due to band reconstruction (ringing); caller clamps in the view
 *   transform. This module deliberately does NOT tone-map — that's a
 *   pipeline stage downstream and orthogonal to the fusion math.
 */

import {
    guidedFilter,
    luminanceFromRGBA,
    selectGuideIndex,
    defaultRadius as defaultGuidedRadius,
    clampRadius as clampGuidedRadius,
    DEFAULT_GUIDED_EPS,
} from './guided-filter.js';
import {
    pixelMedianRGBA,
    mixMapFromWeights,
    applyHybridBlendInPlace,
} from './median-stack.js';

const DEFAULT_LEVELS = 5;
const MIN_LEVELS = 3;
const MAX_LEVELS = 7;
/* Below this, we stop downsampling. Codex HDR-A review fix: relaxed from
 * 4 to 2 so a 10×10 image yields the prescribed 4-level chain
 * 10→5→3→2 instead of the truncated 10→5. The 5-tap binomial still has
 * meaningful neighbourhood at 2×2 — anything smaller has no neighbours at
 * all and the Laplacian pass would degenerate. */
const MIN_DIM = 2;
const EPS = 1e-12;
/* WEIGHT_FLOOR is the per-component floor in computeWeights. The original
 * Mertens stability epsilon (1e-12) is mathematically fine, but in flat
 * regions where contrast=sat=0 it produces rawWeight ≈ 1e-24·wexp which
 * underflows the EPS=1e-12 guard inside normalizeWeights, collapsing the
 * normalised weights to ~0 and washing fused output toward zero. With
 * WEIGHT_FLOOR=1e-4 the smallest rawWeight in flat regions is ~1e-8·wexp,
 * still small but well above the normalize EPS so the wexp ratio between
 * brackets is preserved. Non-flat regions are unaffected (component
 * values dominate the floor). Mirrored on GPU. */
const WEIGHT_FLOOR = 1e-4;

/* 5-tap binomial kernel [1,4,6,4,1] / 16. Normalised so DC is preserved. */
const BINOM = [1 / 16, 4 / 16, 6 / 16, 4 / 16, 1 / 16];

/* Well-exposedness defaults — Codex R1 lock-in.
 * Log-luminance Gaussian centred on scene mid-grey. μ=log2(0.18) ≈ −2.4739
 * is the principled scene-linear target (the original Mertens μ=0.5 lives
 * in display-domain, which has different gamma assumptions). σ=2 stops
 * gives a smooth fall-off ~e^(−1/2) at ±2 EV from mid-grey. EV_FLOOR
 * keeps log2 finite at deep black. */
export const DEFAULT_MU_EV = Math.log2(0.18);
export const DEFAULT_SIGMA_EV = 2.0;
export const DEFAULT_EV_FLOOR = 1e-6;

/* Highlight saturation guard. wexp is multiplicatively damped to 0 as
 * max(R,G,B) crosses SAT_LOW → SAT_HIGH. Below SAT_LOW the guard is
 * a no-op (well-exposed pixels keep their full log-EV weight). */
const SAT_LOW = 0.95;
const SAT_HIGH = 1.0;

/* Phase 1.H — local-stdev contrast metric (enfuse-style). Default radius 2
 * yields a 5×5 window — same neighbourhood footprint as the 5-tap Laplacian
 * so the two contrast metrics live on the same spatial scale. */
const DEFAULT_STDEV_RADIUS = 2;

/**
 * Build the Laplacian-magnitude contrast weight. Extracted verbatim from the
 * inline contrast pass so the byte-identity baseline (default contrastMode)
 * stays intact. Edges (x=0, x=W-1, y=0, y=H-1) keep contrast=0 — same as a
 * clamp boundary.
 *
 * IMPORTANT: the loop body is byte-identical to the original inline code at
 * the previous mertens.js:133-143. Do NOT alter the order of operands or
 * sign convention — the existing pixel-hash + worker-parity baselines were
 * recorded against this exact arithmetic.
 *
 * @param {Float32Array} lum    width*height single-channel luminance
 * @param {number} width
 * @param {number} height
 * @returns {Float32Array}      width*height contrast (abs(Laplacian))
 */
export function buildLaplacianWeight(lum, width, height) {
    const N = width * height;
    const contrast = new Float32Array(N);
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const k = y * width + x;
            const lap =
                  -lum[k - width] - lum[k + width]
                  - lum[k - 1]    - lum[k + 1]
                  + 4 * lum[k];
            contrast[k] = Math.abs(lap);
        }
    }
    return contrast;
}

/**
 * Build the local-stdev contrast weight (enfuse). For each pixel, computes
 * the standard deviation of luminance over a (2r+1)×(2r+1) window centred
 * on that pixel, using integral images for O(N) total cost regardless of r.
 *
 * Algorithm (He-Sun-Tang style integral-image trick):
 *   1. Build prefix-sum integral images of lum and lum² (size (W+1)×(H+1)).
 *   2. For each pixel, sample the 4 corners → window sum, window sumSq.
 *   3. variance = E[X²] − E[X]² (clamped ≥0 to absorb FP slack).
 *   4. stdev = sqrt(variance).
 *
 * Boundary: window clamps to image bounds; the divisor is the actual sample
 * count (NOT zero-padded), matching guided-filter.js boxFilter() convention.
 * This avoids artificially darkening edge stdev and keeps the metric a
 * meaningful focus measure right up to the borders.
 *
 * Output shape matches buildLaplacianWeight (Float32Array of length
 * width*height). Caller treats both interchangeably as the contrast term in
 * computeWeights.
 *
 * @param {Float32Array} lum    width*height single-channel luminance
 * @param {number} width
 * @param {number} height
 * @param {number} [radius=DEFAULT_STDEV_RADIUS]   box radius (window=2r+1)
 * @returns {Float32Array}      width*height local-stdev weight
 */
export function buildStdevWeight(lum, width, height, radius) {
    const N = width * height;
    const r = Number.isFinite(radius) && radius >= 1
        ? Math.round(radius)
        : DEFAULT_STDEV_RADIUS;

    /* Integral images sized (W+1) × (H+1). Row 0 / col 0 are zeros so the
     * standard 4-corner formula
     *   sum(x0..x1, y0..y1) = I[y1+1][x1+1] − I[y0][x1+1] − I[y1+1][x0] + I[y0][x0]
     * works without a separate first-row/col branch. Float64 to avoid
     * cumulative round-off across ~256k samples; the final cast back to
     * Float32 happens once at write-out. */
    const stride = width + 1;
    const sumI  = new Float64Array(stride * (height + 1));
    const sumI2 = new Float64Array(stride * (height + 1));
    for (let y = 0; y < height; y++) {
        let rowSum = 0;
        let rowSumSq = 0;
        const baseDst = (y + 1) * stride;
        const baseSrc = y * width;
        const basePrev = y * stride;
        for (let x = 0; x < width; x++) {
            const v = lum[baseSrc + x];
            rowSum   += v;
            rowSumSq += v * v;
            sumI [baseDst + (x + 1)] = sumI [basePrev + (x + 1)] + rowSum;
            sumI2[baseDst + (x + 1)] = sumI2[basePrev + (x + 1)] + rowSumSq;
        }
    }

    const out = new Float32Array(N);
    for (let y = 0; y < height; y++) {
        const y0 = y - r > 0 ? y - r : 0;
        const y1 = y + r < height - 1 ? y + r : height - 1;
        for (let x = 0; x < width; x++) {
            const x0 = x - r > 0 ? x - r : 0;
            const x1 = x + r < width - 1 ? x + r : width - 1;

            const a = y0 * stride + x0;
            const b = y0 * stride + (x1 + 1);
            const c = (y1 + 1) * stride + x0;
            const d = (y1 + 1) * stride + (x1 + 1);
            const winSum   = sumI [d] - sumI [b] - sumI [c] + sumI [a];
            const winSumSq = sumI2[d] - sumI2[b] - sumI2[c] + sumI2[a];
            const count = (x1 - x0 + 1) * (y1 - y0 + 1);
            const mean = winSum / count;
            const variance = winSumSq / count - mean * mean;
            /* FP slack can drive variance slightly negative on flat patches.
             * Clamp ≥0 before sqrt so the output is finite and ≥0. */
            out[y * width + x] = Math.sqrt(variance > 0 ? variance : 0);
        }
    }
    return out;
}

/**
 * Per-pixel weights = w_contrast^α · w_saturation^β · w_exposure^γ.
 * Each component is in [0, 1]; default exponents 1, 1, 1.
 *
 * Well-exposedness (Codex R1 lock-in): log-luminance Gaussian centred on
 * scene mid-grey. EV = log2(max(L, EV_FLOOR)); wexp = exp(−(EV−μ_EV)²/(2σ_EV²)).
 *
 * Why log-luminance and not per-channel product:
 *   The previous per-channel product fr·fg·fb in linear-light penalised
 *   any pixel where the three channels disagreed near μ, biasing flat
 *   regions toward whichever bracket happened to have channels closest
 *   to μ — visible as "mid-bracket dominance" on real triplets. Working
 *   in log-luminance puts exposure on the scale that "stops" actually
 *   live on (EV-symmetric), and using a single luminance scalar removes
 *   the spurious per-channel coupling.
 *
 * Saturation is still computed because it's a separate criterion (we
 * want vivid pixels to score high regardless of exposure).
 *
 * @param {Float32Array} rgba   width*height*4, linear ACEScg
 * @param {number} width
 * @param {number} height
 * @param {object} [opts]       { contrast, saturation, exposure } exponents
 *                              + optional { muEV, sigEV, evFloor }
 *                              + Phase 1.H: { contrastMode, stdevRadius }.
 *                              contrastMode='laplacian' (default, byte-
 *                              identity preserved) or 'stdev' (enfuse local-
 *                              stdev focus metric, 5×5 default).
 * @returns {Float32Array}      width*height single-channel weights.
 */
export function computeWeights(rgba, width, height, opts = {}) {
    const wContrast   = opts.contrast   != null ? opts.contrast   : 1;
    const wSaturation = opts.saturation != null ? opts.saturation : 1;
    const wExposure   = opts.exposure   != null ? opts.exposure   : 1;
    const muEV    = opts.muEV    != null ? opts.muEV    : DEFAULT_MU_EV;
    const sigEV   = opts.sigEV   != null ? opts.sigEV   : DEFAULT_SIGMA_EV;
    const evFloor = opts.evFloor != null ? opts.evFloor : DEFAULT_EV_FLOOR;
    /* Phase 2.A.2 robust-mode hook: caller can lift the per-component
     * floor (default WEIGHT_FLOOR=1e-4) to reduce per-pixel weight peakiness
     * in flat regions. Codex 2.A.2.design D6 finalised 3e-4 for robust mode. */
    const weightFloor = opts.weightFloor != null ? opts.weightFloor : WEIGHT_FLOOR;
    const inv2sig2 = 1 / (2 * sigEV * sigEV);

    const N = width * height;
    const out = new Float32Array(N);

    /* Luminance (Rec.709 weights — used as a perceptual-ish scalar even
     * in ACEScg primaries; this is contrast detection, not colorimetry). */
    const lum = new Float32Array(N);
    for (let i = 0, p = 0; i < N; i++, p += 4) {
        lum[i] = 0.2126 * rgba[p] + 0.7152 * rgba[p + 1] + 0.0722 * rgba[p + 2];
    }

    /* Phase 1.H — contrast metric is selectable. Default 'laplacian' is the
     * legacy abs-Laplacian 5-tap (byte-identity preserved). 'stdev' is the
     * enfuse-style local stdev which focus-stacking benefits from (per-pixel
     * sharpest-bracket selection). 'hardmask' is Phase B deferred — the
     * binary winner-take-all mode would land at the pyramid level loop in
     * fuseBrackets, not here, so for now it falls back to the laplacian
     * metric and the higher level wires the actual mask logic later. */
    const contrastMode = opts.contrastMode === 'stdev' ? 'stdev' : 'laplacian';
    const stdevRadius  = opts.stdevRadius != null ? opts.stdevRadius : DEFAULT_STDEV_RADIUS;
    const contrast = contrastMode === 'stdev'
        ? buildStdevWeight(lum, width, height, stdevRadius)
        : buildLaplacianWeight(lum, width, height);

    for (let i = 0, p = 0; i < N; i++, p += 4) {
        const r = rgba[p], g = rgba[p + 1], b = rgba[p + 2];
        const m = (r + g + b) / 3;
        const dr = r - m, dg = g - m, db = b - m;
        const sat = Math.sqrt((dr * dr + dg * dg + db * db) / 3);

        /* Well-exposedness: log-luminance Gaussian about μ_EV (Codex R1).
         * EV_FLOOR keeps log2 finite for deep-black pixels; effective
         * range is log2(EV_FLOOR) ≈ −19.93, well below any realistic
         * scene radiance. */
        const L = lum[i] > evFloor ? lum[i] : evFloor;
        const ev = Math.log2(L);
        const dEV = ev - muEV;
        let wexp = Math.exp(-(dEV * dEV) * inv2sig2);

        /* Highlight saturation guard. The luminance-only well-exposedness
         * (Codex R1) lost the per-channel clip-handling that the original
         * Mertens fr·fg·fb product provided as a side effect. Without it,
         * over-exposed brackets contribute strongly even where individual
         * channels are clipped (max(R,G,B) ≈ 1.0), producing per-channel
         * Laplacian-band overshoot that shows up as chromatic ringing
         * (orange/cyan rings around bright highlights).
         *
         * Mitigation: damp wexp smoothly toward 0 as max(R,G,B) crosses
         * SAT_LOW → SAT_HIGH. The damping is multiplicative on top of
         * the log-EV Gaussian, so well-exposed pixels see no change and
         * only near-clipped pixels lose contribution. */
        const maxC = r > g ? (r > b ? r : b) : (g > b ? g : b);
        if (maxC > SAT_LOW) {
            const t = maxC >= SAT_HIGH ? 1 : (maxC - SAT_LOW) / (SAT_HIGH - SAT_LOW);
            const smooth = t * t * (3 - 2 * t);
            wexp *= 1 - smooth;
        }

        out[i] =
              Math.pow(contrast[i] + weightFloor, wContrast)
            * Math.pow(sat        + weightFloor, wSaturation)
            * Math.pow(wexp       + weightFloor, wExposure);
    }
    return out;
}

/**
 * Normalize stack of per-bracket weights so they sum to 1 per pixel.
 * @param {Float32Array[]} weights   array of width*height single-channel maps
 * @returns {Float32Array[]} new arrays (immutable input)
 */
export function normalizeWeights(weights) {
    if (weights.length === 0) return [];
    const N = weights[0].length;
    const out = weights.map(() => new Float32Array(N));
    for (let i = 0; i < N; i++) {
        let sum = 0;
        for (let k = 0; k < weights.length; k++) sum += weights[k][i];
        const inv = 1 / (sum + EPS);
        for (let k = 0; k < weights.length; k++) out[k][i] = weights[k][i] * inv;
    }
    return out;
}

/* ── Pyramid primitives ──────────────────────────────────────────────── */

/**
 * Reflective boundary index: x in [0, n-1].
 *
 * Switched from edge-clamp to reflective in this revision. Edge-clamp
 * left a periodic sawtooth/zigzag at the very top row of fused output
 * on real bracket triplets — at the deepest pyramid levels the 5-tap
 * binomial blur taps would all return the same edge value, producing
 * no smoothing and preserving high-frequency content there. Reflective
 * boundary mirrors taps inward and gives correct DC behaviour at the
 * boundary, eliminating the artifact.
 *
 * Iterative reflect handles cases where the offset is larger than n
 * (only matters for very small pyramid levels). For typical 5-tap
 * blur with offsets in [-2, +2], single reflection always suffices.
 */
function clampIdx(x, n) {
    if (n <= 1) return 0;
    /* Loop terminates because each step strictly reduces |x − [0, n-1]|. */
    while (x < 0 || x >= n) {
        if (x < 0)      x = -x;
        else            x = 2 * (n - 1) - x;
    }
    return x;
}

/**
 * Compute the dimensions for each pyramid level. Level 0 is full size;
 * each subsequent level is ceil(prev/2). Stops when either dimension
 * would drop below MIN_DIM, capped at `levels`.
 */
function pyramidDims(width, height, levels) {
    const dims = [];
    let w = width, h = height;
    for (let L = 0; L < levels; L++) {
        dims.push({ w, h });
        if (L === levels - 1) break;
        const nw = (w + 1) >> 1;
        const nh = (h + 1) >> 1;
        if (nw < MIN_DIM || nh < MIN_DIM) break;
        w = nw; h = nh;
    }
    return dims;
}

/**
 * Apply 5-tap binomial separable filter to a single-channel float buffer.
 * Returns a new same-size buffer. Boundary: clamp.
 */
function blur1(buf, w, h, tmp) {
    const N = w * h;
    const horiz = tmp && tmp.length === N ? tmp : new Float32Array(N);
    /* Horizontal pass */
    for (let y = 0; y < h; y++) {
        const row = y * w;
        for (let x = 0; x < w; x++) {
            const k = row + x;
            horiz[k] =
                  BINOM[0] * buf[row + clampIdx(x - 2, w)]
                + BINOM[1] * buf[row + clampIdx(x - 1, w)]
                + BINOM[2] * buf[row + x]
                + BINOM[3] * buf[row + clampIdx(x + 1, w)]
                + BINOM[4] * buf[row + clampIdx(x + 2, w)];
        }
    }
    /* Vertical pass */
    const out = new Float32Array(N);
    for (let y = 0; y < h; y++) {
        const ym2 = clampIdx(y - 2, h) * w;
        const ym1 = clampIdx(y - 1, h) * w;
        const y0  = y * w;
        const yp1 = clampIdx(y + 1, h) * w;
        const yp2 = clampIdx(y + 2, h) * w;
        for (let x = 0; x < w; x++) {
            out[y0 + x] =
                  BINOM[0] * horiz[ym2 + x]
                + BINOM[1] * horiz[ym1 + x]
                + BINOM[2] * horiz[y0  + x]
                + BINOM[3] * horiz[yp1 + x]
                + BINOM[4] * horiz[yp2 + x];
        }
    }
    return out;
}

/**
 * Apply 5-tap binomial separable filter to an RGBA float buffer.
 * Channels processed in-place per element; alpha included for symmetry.
 */
function blur4(rgba, w, h) {
    const N = w * h;
    const horiz = new Float32Array(N * 4);
    /* Horizontal pass — 4 channels packed. */
    for (let y = 0; y < h; y++) {
        const row = y * w * 4;
        for (let x = 0; x < w; x++) {
            const xm2 = clampIdx(x - 2, w) * 4;
            const xm1 = clampIdx(x - 1, w) * 4;
            const x0  = x * 4;
            const xp1 = clampIdx(x + 1, w) * 4;
            const xp2 = clampIdx(x + 2, w) * 4;
            const o = row + x0;
            horiz[o]     = BINOM[0]*rgba[row+xm2]   + BINOM[1]*rgba[row+xm1]   + BINOM[2]*rgba[row+x0]   + BINOM[3]*rgba[row+xp1]   + BINOM[4]*rgba[row+xp2];
            horiz[o + 1] = BINOM[0]*rgba[row+xm2+1] + BINOM[1]*rgba[row+xm1+1] + BINOM[2]*rgba[row+x0+1] + BINOM[3]*rgba[row+xp1+1] + BINOM[4]*rgba[row+xp2+1];
            horiz[o + 2] = BINOM[0]*rgba[row+xm2+2] + BINOM[1]*rgba[row+xm1+2] + BINOM[2]*rgba[row+x0+2] + BINOM[3]*rgba[row+xp1+2] + BINOM[4]*rgba[row+xp2+2];
            horiz[o + 3] = BINOM[0]*rgba[row+xm2+3] + BINOM[1]*rgba[row+xm1+3] + BINOM[2]*rgba[row+x0+3] + BINOM[3]*rgba[row+xp1+3] + BINOM[4]*rgba[row+xp2+3];
        }
    }
    /* Vertical pass */
    const out = new Float32Array(N * 4);
    for (let y = 0; y < h; y++) {
        const ym2 = clampIdx(y - 2, h) * w * 4;
        const ym1 = clampIdx(y - 1, h) * w * 4;
        const y0  = y * w * 4;
        const yp1 = clampIdx(y + 1, h) * w * 4;
        const yp2 = clampIdx(y + 2, h) * w * 4;
        for (let x = 0; x < w; x++) {
            const xo = x * 4;
            const o = y0 + xo;
            out[o]     = BINOM[0]*horiz[ym2+xo]   + BINOM[1]*horiz[ym1+xo]   + BINOM[2]*horiz[y0+xo]   + BINOM[3]*horiz[yp1+xo]   + BINOM[4]*horiz[yp2+xo];
            out[o + 1] = BINOM[0]*horiz[ym2+xo+1] + BINOM[1]*horiz[ym1+xo+1] + BINOM[2]*horiz[y0+xo+1] + BINOM[3]*horiz[yp1+xo+1] + BINOM[4]*horiz[yp2+xo+1];
            out[o + 2] = BINOM[0]*horiz[ym2+xo+2] + BINOM[1]*horiz[ym1+xo+2] + BINOM[2]*horiz[y0+xo+2] + BINOM[3]*horiz[yp1+xo+2] + BINOM[4]*horiz[yp2+xo+2];
            out[o + 3] = BINOM[0]*horiz[ym2+xo+3] + BINOM[1]*horiz[ym1+xo+3] + BINOM[2]*horiz[y0+xo+3] + BINOM[3]*horiz[yp1+xo+3] + BINOM[4]*horiz[yp2+xo+3];
        }
    }
    return out;
}

/**
 * Stride-2 downsample (already-blurred buffer in). Single-channel.
 * Output dims = ceil(w/2) × ceil(h/2). Picks pixel (2x, 2y).
 */
function downsample1(blurred, w, h) {
    const dw = (w + 1) >> 1;
    const dh = (h + 1) >> 1;
    const out = new Float32Array(dw * dh);
    for (let y = 0; y < dh; y++) {
        const sy = clampIdx(y * 2, h);
        for (let x = 0; x < dw; x++) {
            const sx = clampIdx(x * 2, w);
            out[y * dw + x] = blurred[sy * w + sx];
        }
    }
    return out;
}

/** Stride-2 downsample for RGBA. */
function downsample4(blurred, w, h) {
    const dw = (w + 1) >> 1;
    const dh = (h + 1) >> 1;
    const out = new Float32Array(dw * dh * 4);
    for (let y = 0; y < dh; y++) {
        const sy = clampIdx(y * 2, h);
        for (let x = 0; x < dw; x++) {
            const sx = clampIdx(x * 2, w);
            const si = (sy * w + sx) * 4;
            const di = (y * dw + x) * 4;
            out[di]     = blurred[si];
            out[di + 1] = blurred[si + 1];
            out[di + 2] = blurred[si + 2];
            out[di + 3] = blurred[si + 3];
        }
    }
    return out;
}

/**
 * Upsample single-channel: zero-insert by 2 then blur with 4× binomial
 * to compensate for the energy lost. Output size = (dstW, dstH) which
 * the caller specifies (NOT always 2× input — last level may be odd).
 */
function upsample1(src, sw, sh, dstW, dstH) {
    const N = dstW * dstH;
    const expanded = new Float32Array(N);
    /* Insert source samples at even positions, zero elsewhere. */
    for (let y = 0; y < sh; y++) {
        const dy = y * 2;
        if (dy >= dstH) break;
        for (let x = 0; x < sw; x++) {
            const dx = x * 2;
            if (dx >= dstW) break;
            expanded[dy * dstW + dx] = src[y * sw + x] * 4;
        }
    }
    /* Blur with same binomial — energy preserved because we pre-mult by 4. */
    return blur1(expanded, dstW, dstH);
}

/** Upsample RGBA. Same approach as upsample1, packed 4-wide. */
function upsample4(src, sw, sh, dstW, dstH) {
    const expanded = new Float32Array(dstW * dstH * 4);
    for (let y = 0; y < sh; y++) {
        const dy = y * 2;
        if (dy >= dstH) break;
        for (let x = 0; x < sw; x++) {
            const dx = x * 2;
            if (dx >= dstW) break;
            const si = (y * sw + x) * 4;
            const di = (dy * dstW + dx) * 4;
            expanded[di]     = src[si]     * 4;
            expanded[di + 1] = src[si + 1] * 4;
            expanded[di + 2] = src[si + 2] * 4;
            expanded[di + 3] = src[si + 3] * 4;
        }
    }
    return blur4(expanded, dstW, dstH);
}

function clampLevels(levels) {
    if (levels == null) return DEFAULT_LEVELS;
    const n = levels | 0;
    if (n < MIN_LEVELS) return MIN_LEVELS;
    if (n > MAX_LEVELS) return MAX_LEVELS;
    return n;
}

/**
 * Build a Gaussian pyramid for a single-channel float buffer.
 * @param {Float32Array} buf
 * @param {number} width
 * @param {number} height
 * @param {number} [levels=5]
 * @returns {Array<{ data: Float32Array, w: number, h: number }>} length ≤ levels
 */
export function gaussianPyramid(buf, width, height, levels, signal) {
    const L = clampLevels(levels);
    const dims = pyramidDims(width, height, L);
    const pyr = [{ data: buf.slice(), w: width, h: height }];
    let cur = buf, cw = width, ch = height;
    for (let i = 1; i < dims.length; i++) {
        /* Codex HDR-A review fix: per-level abort checkpoint. */
        checkAbort(signal);
        const blurred = blur1(cur, cw, ch);
        cur = downsample1(blurred, cw, ch);
        cw = dims[i].w; ch = dims[i].h;
        pyr.push({ data: cur, w: cw, h: ch });
    }
    return pyr;
}

/**
 * Build a Laplacian pyramid for an RGBA float buffer.
 * Each level L = G_L - upsample(G_{L+1}); the last level is the residual
 * Gaussian (low-pass remainder).
 *
 * Memory: builds the full Gaussian chain, then converts each non-top
 * level in-place (G_i → L_i = G_i - up(G_{i+1})). Avoids doubling the
 * full-size base level. Caller still owns the input — we slice exactly
 * once for L0 (since we have to mutate it).
 *
 * @param {Float32Array} rgba
 * @param {number} width
 * @param {number} height
 * @param {number} [levels=5]
 * @returns {Array<{ data: Float32Array, w: number, h: number }>}
 */
export function laplacianPyramid(rgba, width, height, levels, signal) {
    const L = clampLevels(levels);
    const dims = pyramidDims(width, height, L);
    /* Build Gaussian pyramid on RGBA first. L0 is sliced (we'll overwrite
     * it when computing L0 = G0 - up(G1)) — caller's input is preserved. */
    const gauss = [{ data: rgba.slice(), w: width, h: height }];
    let cur = gauss[0].data, cw = width, ch = height;
    for (let i = 1; i < dims.length; i++) {
        /* Codex HDR-A review fix: per-level abort checkpoint. */
        checkAbort(signal);
        const blurred = blur4(cur, cw, ch);
        cur = downsample4(blurred, cw, ch);
        cw = dims[i].w; ch = dims[i].h;
        gauss.push({ data: cur, w: cw, h: ch });
    }
    /* Convert to Laplacian in place: each level except the top is replaced
     * by L_i = G_i - up(G_{i+1}). Top stays as residual Gaussian. */
    for (let i = 0; i < gauss.length - 1; i++) {
        checkAbort(signal);
        const g_i = gauss[i];
        const g_next = gauss[i + 1];
        const up = upsample4(g_next.data, g_next.w, g_next.h, g_i.w, g_i.h);
        const buf = g_i.data;
        const N4 = g_i.w * g_i.h * 4;
        for (let k = 0; k < N4; k++) buf[k] -= up[k];
    }
    return gauss;
}

/**
 * Collapse a Laplacian pyramid back to full resolution.
 * Walks coarsest → finest: cur = L_i + upsample(cur).
 *
 * @param {Array<{data:Float32Array,w:number,h:number}>} pyramid RGBA
 * @returns {{ data: Float32Array, w: number, h: number }}
 */
export function collapsePyramid(pyramid, signal) {
    if (!pyramid || pyramid.length === 0) {
        throw new Error('collapsePyramid: empty pyramid');
    }
    let cur = pyramid[pyramid.length - 1].data.slice();
    let cw = pyramid[pyramid.length - 1].w;
    let ch = pyramid[pyramid.length - 1].h;
    for (let i = pyramid.length - 2; i >= 0; i--) {
        /* Codex HDR-A review fix: per-level abort checkpoint. */
        checkAbort(signal);
        const next = pyramid[i];
        const up = upsample4(cur, cw, ch, next.w, next.h);
        const N4 = next.w * next.h * 4;
        const sum = new Float32Array(N4);
        for (let k = 0; k < N4; k++) sum[k] = next.data[k] + up[k];
        cur = sum;
        cw = next.w; ch = next.h;
    }
    return { data: cur, w: cw, h: ch };
}

/**
 * Internal collapse that mutates the merged pyramid in place. Used only
 * by fuseBrackets — saves one full-size Float32 allocation at the cost
 * of consuming the input. Public callers use collapsePyramid (immutable).
 */
function collapsePyramidInPlace(pyramid, signal) {
    if (!pyramid || pyramid.length === 0) {
        throw new Error('collapsePyramidInPlace: empty pyramid');
    }
    let cur = pyramid[pyramid.length - 1].data;
    let cw = pyramid[pyramid.length - 1].w;
    let ch = pyramid[pyramid.length - 1].h;
    for (let i = pyramid.length - 2; i >= 0; i--) {
        /* Codex HDR-A review fix: per-level abort checkpoint. */
        checkAbort(signal);
        const next = pyramid[i];
        const up = upsample4(cur, cw, ch, next.w, next.h);
        const dst = next.data;
        const N4 = next.w * next.h * 4;
        for (let k = 0; k < N4; k++) dst[k] += up[k];
        /* Free the consumed coarser level. */
        pyramid[i + 1].data = null;
        cur = dst;
        cw = next.w; ch = next.h;
    }
    return { data: cur, w: cw, h: ch };
}

/* ── Public fusion entry-point ──────────────────────────────────────── */

function checkAbort(signal) {
    if (signal && signal.aborted) {
        const err = new Error('fuseBrackets: aborted');
        err.name = 'AbortError';
        throw err;
    }
}

/**
 * Fuse a stack of pixel-aligned brackets into a single RGBA Float32 image.
 *
 * Algorithm:
 *   1. computeWeights per bracket (contrast · sat · well-exposed)
 *   2. normalize across stack (Σ_k weight_k = 1 per pixel)
 *   3. gaussianPyramid of each weight; laplacianPyramid of each RGBA
 *   4. weighted sum at every pyramid level (broadcast scalar weight × RGBA)
 *   5. collapse the merged Laplacian pyramid to full-res RGBA
 *
 * @param {Array<{ rgba: Float32Array, width: number, height: number }>} stack
 * @param {object} [opts]
 *   @prop {number} [levels=5]            pyramid depth (clamped to [3,7])
 *   @prop {number} [contrast=1]
 *   @prop {number} [saturation=1]
 *   @prop {number} [exposure=1]
 *   @prop {number} [muEV=log2(0.18)]     well-exposedness centre (log2 stops)
 *   @prop {number} [sigEV=2.0]           well-exposedness sigma   (stops)
 *   @prop {number} [evFloor=1e-6]        log2 floor at deep black
 *   @prop {Float32Array} [evStops]       EV stops (relative to median = 0).
 *                                        If provided, used to pick the guide
 *                                        bracket as the median-EV bracket.
 *   @prop {number} [guideIndex]          override guide bracket index entirely
 *   @prop {number} [gfRadius]            guided-filter radius (defaults to
 *                                        ~1.75% of short edge, [4,32])
 *   @prop {number} [gfEps=1e-3]          guided-filter regularisation
 *   @prop {boolean} [skipGuidedFilter=false] dev escape hatch — disable the
 *                                        guided-filter pass entirely
 *   @prop {'laplacian'|'stdev'} [contrastMode='laplacian']  Phase 1.H —
 *                                        contrast metric selector. Default
 *                                        'laplacian' is the legacy 5-tap
 *                                        |∇²L| and is byte-identity-preserved
 *                                        for existing baselines. 'stdev'
 *                                        switches to local stdev over a
 *                                        5×5 window (enfuse-style focus
 *                                        metric); use this for focus stacks.
 *                                        ('hardmask' is Phase B deferred.)
 *   @prop {number} [stdevRadius=2]       window radius when contrastMode='stdev'
 *   @prop {AbortSignal} [signal]         caller may cancel between levels
 * @returns {{ rgba: Float32Array, width: number, height: number }}
 *          RGBA Float32, alpha forced to 1.0 (synthetic — fused alpha is
 *          undefined across exposures, caller's sidecar carries any mask).
 */
export function fuseBrackets(stack, opts = {}) {
    if (!Array.isArray(stack) || stack.length < 2) {
        throw new Error('fuseBrackets: need at least 2 brackets');
    }
    const { width, height } = stack[0];
    for (const b of stack) {
        if (!b || !b.rgba) throw new Error('fuseBrackets: invalid bracket entry');
        if (b.width !== width || b.height !== height) {
            throw new Error('fuseBrackets: all brackets must share dimensions');
        }
        if (b.rgba.length !== width * height * 4) {
            throw new Error('fuseBrackets: bracket rgba length mismatch');
        }
    }
    const signal = opts.signal;
    const levels = clampLevels(opts.levels);
    const muEV    = opts.muEV    != null ? opts.muEV    : DEFAULT_MU_EV;
    const evFloor = opts.evFloor != null ? opts.evFloor : DEFAULT_EV_FLOOR;
    const gfEps   = opts.gfEps   != null ? opts.gfEps   : DEFAULT_GUIDED_EPS;
    /* Codex R3 STOP: must clamp BEFORE pass-through so CPU and GPU share
     * the same effective radius. GPU shaders are hard-capped at MAX_R=32. */
    const gfRadius = Number.isFinite(opts.gfRadius)
        ? clampGuidedRadius(opts.gfRadius)
        : defaultGuidedRadius(width, height);
    const skipGuided = opts.skipGuidedFilter === true;

    /* Phase 2.A.2 — fusionMode: 'camera' | 'robust'.
     * Public/API default = 'camera' (preserves byte-identity for existing
     * tests + callers that omit fusionMode). UI default in
     * hdr-merge-modal.js is 'auto' which resolves to one of these BEFORE
     * fuseBrackets runs (Codex 2.A.2.design D2 + HIGH-2 lock-in).
     *
     * 'auto' here is unsupported — the merge.js layer must resolve it to
     * 'camera' or 'robust' via auto-detect.js BEFORE entering this function.
     * NB: fusionMode is the camera/robust axis, distinct from
     * backend.js:fuseBracketsAuto which is the GPU/CPU axis. */
    const fusionMode = opts.fusionMode === 'robust' ? 'robust' : 'camera';
    /* Robust mode: lift WEIGHT_FLOOR from 1e-4 → 3e-4 globally (Codex D6). */
    const robustFloor = 3e-4;
    const computeOpts = fusionMode === 'robust'
        ? { ...opts, weightFloor: robustFloor }
        : opts;

    /* 1a. Per-bracket RAW weights (Codex R1: filter then normalize once).
     *     The same opts object passes muEV/sigEV/evFloor through to the
     *     log-luminance Gaussian inside computeWeights. */
    checkAbort(signal);
    const rawWeights = stack.map(b => computeWeights(b.rgba, width, height, computeOpts));

    /* 1b. Smooth raw weights along guide-image structure so coarse-scale
     *     weight discontinuities don't seed Laplacian-band ringing.
     *     Guide selection (Codex R1):
     *       - if evStops/guideIndex given: use the median-EV bracket
     *       - else: bracket whose mean log-luminance is closest to muEV
     *     Single guide is shared across all brackets — picking per-bracket
     *     guides would re-introduce the structure we're trying to smooth. */
    checkAbort(signal);
    let filtered = rawWeights;
    if (!skipGuided) {
        const guideIdx = Number.isInteger(opts.guideIndex)
                         && opts.guideIndex >= 0
                         && opts.guideIndex < stack.length
            ? opts.guideIndex
            : selectGuideIndex(stack, opts.evStops || null, muEV, evFloor);
        const guide = luminanceFromRGBA(
            stack[guideIdx].rgba, width, height,
        );
        filtered = rawWeights.map(w =>
            guidedFilter(w, guide, width, height, gfRadius, gfEps),
        );
        /* The raw weights are now superseded — drop references to free GC. */
        for (let k = 0; k < rawWeights.length; k++) rawWeights[k] = null;
    }

    /* 1c. Single normalize pass on the filtered weights. */
    checkAbort(signal);
    const weights = normalizeWeights(filtered);
    /* Drop intermediate references. */
    rawWeights.length = 0;
    for (let k = 0; k < filtered.length; k++) filtered[k] = null;

    /* 2. Allocate the merged Laplacian pyramid (one full chain). All
     *    bracket contributions accumulate into this; per-bracket pyramids
     *    are built one-at-a-time and freed by GC after each. */
    const dims = pyramidDims(width, height, levels);
    const merged = dims.map(d => ({
        data: new Float32Array(d.w * d.h * 4),
        w: d.w,
        h: d.h,
    }));

    /* Phase 2.A.2 robust-mode scratch — collect coarsest-level data per
     * bracket so the hybrid base-level blend (after this loop) can median
     * the brackets at the top of the pyramid. In camera mode these stay
     * null and the path is byte-identical to r6. */
    const topIdx = merged.length - 1;
    const topW   = fusionMode === 'robust' ? new Array(stack.length) : null;
    const topG   = fusionMode === 'robust' ? new Array(stack.length) : null;

    /* 3. Per bracket: build weight Gaussian pyramid + RGBA Laplacian
     *    pyramid, accumulate, then drop both before next iteration. This
     *    keeps peak memory ≈ merged + one bracket's pyramids, NOT N×. */
    for (let k = 0; k < stack.length; k++) {
        checkAbort(signal);
        const wPyr = gaussianPyramid(weights[k], width, height, levels, signal);
        /* Free this bracket's weight buffer — pyramid has its own copy. */
        weights[k] = null;
        checkAbort(signal);
        const lap = laplacianPyramid(stack[k].rgba, width, height, levels, signal);
        for (let L = 0; L < merged.length; L++) {
            const lvl = lap[L];
            const w = wPyr[L].data;
            const dst = merged[L].data;
            const px = lvl.w * lvl.h;
            for (let i = 0, p = 0; i < px; i++, p += 4) {
                const wi = w[i];
                dst[p]     += lvl.data[p]     * wi;
                dst[p + 1] += lvl.data[p + 1] * wi;
                dst[p + 2] += lvl.data[p + 2] * wi;
                dst[p + 3] += lvl.data[p + 3] * wi;
            }
            /* Robust mode: capture coarsest-level weight + Gaussian-of-bracket
             * BEFORE we null lvl.data / wPyr[L].data. The Mertens convention
             * has lapPyramid[top] === gaussianPyramid[top] (residual), so
             * `lvl.data` at the top level IS the bracket's coarsest Gaussian. */
            if (fusionMode === 'robust' && L === topIdx) {
                topW[k] = new Float32Array(w);
                topG[k] = new Float32Array(lvl.data);
            }
            /* Drop level data as we consume it — earliest GC opportunity. */
            lvl.data = null;
            wPyr[L].data = null;
        }
    }

    /* 3.5. Robust-mode hybrid base-level blend (Codex 2.A.2.design D1).
     *      finalTop[px] = lerp(softBlendTop[px], medianTop[px], mix[px])
     *      where mix = clamp((1 − maxW) / (1 − 1/N), 0, 1).
     *      Skipped entirely in camera mode → byte-identity preserved. */
    if (fusionMode === 'robust' && topW && topG) {
        checkAbort(signal);
        const topDims = merged[topIdx];
        const medianTop = pixelMedianRGBA(topG, topDims.w, topDims.h);
        const mix = mixMapFromWeights(topW, topDims.w * topDims.h);
        applyHybridBlendInPlace(merged[topIdx].data, medianTop, mix, topDims.w, topDims.h);
        /* Free scratch — large arrays at coarse level but still worth it. */
        for (let k = 0; k < topW.length; k++) { topW[k] = null; topG[k] = null; }
    }

    /* 4. Collapse merged pyramid back to full resolution. Internal
     *    in-place variant frees each coarser level as it's consumed and
     *    reuses the next finer level as the accumulator. */
    checkAbort(signal);
    const collapsed = collapsePyramidInPlace(merged, signal);

    /* 5. Force synthetic alpha=1; band reconstruction can drift it. */
    const out = collapsed.data;
    for (let p = 3; p < out.length; p += 4) out[p] = 1;

    return { rgba: out, width: collapsed.w, height: collapsed.h };
}
